import { customElement, state } from 'lit/decorators.js';
import { OutlineElement } from '../../base/outline-element/outline-element';
import { TemplateResult, html } from 'lit';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';

import '../tufts-tabs/tufts-tab-group/tufts-tab-group';
import '../tufts-tabs/tufts-tab/tufts-tab';

/**
 * This component is used to parse Drupal markup and generate a tab component.
 *
 * It was proving difficult in Drupal's Layout Builder to generate the markup, so this component processes the DOM instead.
 */
@customElement('tufts-tabs-section')
export class TuftsTabsSection extends OutlineElement {
  @state() tabSections: { title?: string; content: HTMLElement }[] = [];

  // This component should not bring all slots into Shadow DOM
  createRenderRoot() {
    return this;
  }

  connectedCallback(): void {
    super.connectedCallback();
    this.initializeTabs();
  }

  initializeTabs(): void {
    // Find the closest ancestor <section> of the component
    const parentSection = this.closest('section');
    if (!parentSection) {
      return;
    }
    // Collect the following sibling <section> elements
    let sibling = parentSection.nextElementSibling;

    while (sibling && sibling.tagName.toLowerCase() === 'section') {
      // When we hit the end section, stop collecting tabs.
      // There could be another group.
      if (sibling.hasAttribute('is-tab-end-section')) {
        sibling.remove();
        break;
      }

      // Drupal layout builder wraps all the content in a `tufts-container` element which we don't want since it applies a background.
      const siblingContent =
        sibling.firstElementChild?.tagName.toLowerCase() === 'tufts-container'
          ? sibling.firstElementChild?.childNodes
          : sibling.childNodes;

      if (siblingContent?.length) {
        const tabSectionContent = document.createElement('div');
        tabSectionContent.append(...siblingContent);
        this.tabSections.push({
          title: sibling.getAttribute('tab-label') ?? undefined,
          content: tabSectionContent,
        });
      }

      const nextSibling = sibling.nextElementSibling;

      sibling.remove();

      sibling = nextSibling;
    }
  }

  render(): TemplateResult {
    return html`
      <tufts-tab-group
        placement="top"
        background-color="transparent"
        container-width="full"
      >
        ${this.tabSections.map((section, index) => {
          const title = section.title ?? 'Tab ' + (index + 1);
          const titleMachineName = title.replace(/\s+/g, '-').toLowerCase();

          return html`
            <tufts-tab slot="nav" panel="${titleMachineName}">
              ${title}
            </tufts-tab>
            <tufts-tab-panel name="${titleMachineName}">
              ${unsafeHTML(`${section.content.innerHTML}`)}
            </tufts-tab-panel>
          `;
        })}
      </tufts-tab-group>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'tufts-tabs-section': TuftsTabsSection;
  }
}
